import { BindAll } from 'lodash-decorators'
import type {
  CityTypeInfo,
  IRecommendCitiesResp,
  ICityDetailResp,
  IGuildMyselfResp,
  GuildDetail,
  GuildMyself,
  AllCities
} from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiTonghao } from '@xt/client/api/kindred-spirit'
import { action, observable, computed, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { getStaticJson } from '@xt/client/utils/get-static-json'
import { dataSource_all_cities } from '@xt/client/dataSource/kindred-spirit/index'
import { getCityCascader, type RawCityItem } from '@xt/client/dataSource/china/index'
import { EnumGuildMemberType, EnumMemberStatus } from '@xt/client/enums/kindred-spirit'
import { UserLocationInfo } from '@xt/client/utils/geojson'
import lodash from 'lodash'

type RecommendCityParamType = {
  adcode: string | number
  recommend?: boolean
  page: number
  pageSize: number
  statusArr: string
}

export enum EnumJoinResponseType {
  Success = 'success',
  NoAuth = 'noAuth'
}

export enum EnumJoinResponseCode {
  NoAuth = 1352100001
}

export enum EnumCityStatus {
  /** 未创建 */
  NotInited = 0,
  /** 助力中 */
  Assistance = 1,
  /** 已创建 */
  Inited = 2
}

@BindAll()
export class Guild {
  constructor() {}

  // 当前成员加入的分桐
  @observable joinedCity: GuildMyself | null = null

  @observable cascadeJson: Array<RawCityItem> = []

  @observable recommend: Array<CityTypeInfo> = []
  @observable locate: CityTypeInfo | null = null
  @observable allCities: Array<AllCities> = []

  @computed
  get isQuitAudit() {
    return this.joinedCity?.memberStatus === EnumMemberStatus.QuitAudit
  }

  @computed
  get firstCity() {
    return this.assembleCities[0]
  }

  @computed
  get resetCities() {
    if (this.assembleCities.length === 0) return []
    return this.assembleCities.slice(1, 8)
  }

  isGuildMyselfResp(item: IGuildMyselfResp | ICityDetailResp): item is IGuildMyselfResp {
    return 'memberType' in item || 'memberStatus' in item
  }

  transformGuild(guild: IGuildMyselfResp | ICityDetailResp): GuildMyself | GuildDetail {
    const base = {
      id: guild.id,
      count: guild.memberCount,
      adcode: guild.adcode,
      cityName: guild.regionName,
      name: guild.guildName,
      description: guild.summary,
      cover: guild.pcCoverPhoto,
      memberIds: guild.previewMemberIds ?? [],
      status: guild.status,
      recruitStatus: guild.recruitStatus,
      appletAvatar: guild.appletAvatar
    }

    if (this.isGuildMyselfResp(guild)) {
      return {
        ...base,
        memberType: guild.memberType,
        memberStatus: guild.memberStatus
      }
    }

    return base
  }

  transformCityInfo(city: Array<IRecommendCitiesResp>): Array<CityTypeInfo> {
    return city.map(i => {
      let subTitle: string = ''

      if (i.status === EnumCityStatus.Inited) {
        subTitle = `${i.memberCount}位桐学加入`
      } else if (i.recruitStatus === true) {
        subTitle = '筹备中...'
      } else if (i.status === EnumCityStatus.Assistance) {
        subTitle = '即将开放...'
      }

      return {
        ...this.transformGuild(i),
        subTitle
      }
    })
  }

  @computed
  get assembleCities() {
    const res = toJS(this.recommend)
    const locate = toJS(this.locate)

    if (!locate) return res

    const existIndex = res.findIndex(item => item.id === locate.id)

    if (existIndex > -1) {
      const existCity = res[existIndex]
      res.splice(existIndex, 1)
      res.splice(1, 0, existCity)
    } else {
      res.splice(1, 0, locate)
    }

    return res
  }

  // 加载推荐和定位guild数据
  async loadRecommendAndLocate(location: UserLocationInfo | null) {
    const res = await this.getRecommendCities()

    if (res.length > 0) {
      this.setRecommend(res)
    }

    this.refreshLocate(location)
  }
  // 刷新定位guild数据
  async refreshLocate(location: UserLocationInfo | null) {
    const local = await this.getLocateCity(location)

    if (local) {
      this.setLocate(local)
    }
  }
  // 城市详情页数据初始化
  async getGuildDetail(guildId: string) {
    if (!guildId) return

    const [, res] = await $service<ICityDetailResp>({
      url: EnumApiTonghao.GuildDetail,
      params: {
        id: guildId
      }
    })

    if (res && res.id) {
      return this.transformGuild(res)
    }
  }
  // 获取推荐分桐
  public async getRecommendCities() {
    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.RecommendCitys,
      params: {
        page: 1,
        pageSize: 8
      }
    })

    if (res && Array.isArray(res.data) && res.data.length) {
      return this.transformCityInfo(res.data)
    }

    return []
  }
  // 获取定位分桐
  public async getLocateCity(location: UserLocationInfo | null) {
    if (!location || !location.cityCode) return null

    const params: RecommendCityParamType = {
      adcode: `${location.cityCode.toString().slice(0, 4)}00`,
      page: 1,
      pageSize: 8,
      statusArr: [EnumCityStatus.Assistance, EnumCityStatus.Inited].join(',') // 1 助力中 2 已创建
    }

    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.GuildList,
      params
    })

    if (res && Array.isArray(res.data) && res.data.length === 1) {
      return this.transformCityInfo(res.data)[0]
    }

    return null
  }

  transformAllCities(city: Array<IRecommendCitiesResp>) {
    const res = lodash.cloneDeep(dataSource_all_cities) as Array<AllCities>

    res.forEach(item => {
      const items = city
        .filter(i => `${i.adcode.slice(0, 2)}0000` === item.parent.adcode)
        .sort((a, b) => Number(b.adcode) - Number(a.adcode))
      item.list = this.transformCityInfo(items)
    })

    return res
  }

  public async loadAllCities() {
    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.GuildList,
      params: {
        page: 1,
        pageSize: 500,
        statusArr: [EnumCityStatus.Assistance, EnumCityStatus.Inited].join(',') // 1 助力中 2 已创建
      }
    })

    if (res && Array.isArray(res.data) && res.data.length > 0) {
      return this.setAllCities(this.transformAllCities(res.data))
    }
  }

  public async loadCascadeJson() {
    if (this.cascadeJson.length > 0) return

    const data = await getStaticJson<RawCityItem[]>(`/mocks/geojson/cities-300.json`)

    if (!data) return

    this.setCascadeJson(data)
  }

  public async loadProvince() {
    await this.loadCascadeJson()

    if (!this.cascadeJson.length) {
      reportEntryLog('topic.load-cascade-cities.error', {
        msg: 'fetch error'
      })
      return null
    }

    return this.cascadeJson.map(item => ({
      label: item.label,
      adcode: item.adcode
    }))
  }

  // 加载城市级联信息
  public async loadCascadeCities() {
    await this.loadCascadeJson()

    if (!this.cascadeJson.length) {
      reportEntryLog('topic.load-cascade-cities.error', {
        msg: 'fetch error'
      })
      return null
    }

    const [, res] = await $service<string[]>({
      url: EnumApiTonghao.GuildEstablished,
      method: 'GET'
    })

    const filters = res && res.length > 0 ? res : []

    console.log('loadCascadeCities', getCityCascader(this.cascadeJson, filters))

    return getCityCascader(this.cascadeJson, filters)
  }
  // 获取当前用户加入的分桐
  public async getHasJoinedCity() {
    const [, res] = await $service<IGuildMyselfResp>({
      url: EnumApiTonghao.Myself
    })

    if (res && res.id) {
      this.setJoinedCity(this.transformGuild(res) as GuildMyself)
      return true
    }

    this.setJoinedCity(null)
    return false
  }

  // 加入桐学会
  public async joinGuild(guildId: string): Promise<EnumJoinResponseType | void> {
    const [err, res] = await $service<boolean>({
      url: EnumApiTonghao.GuildJoin,
      method: 'POST',
      data: {
        guildId
      }
    })

    if (err && err.errCode === EnumJoinResponseCode.NoAuth) {
      return EnumJoinResponseType.NoAuth
    }

    if (res === true) {
      return EnumJoinResponseType.Success
    }
  }

  // 创建桐学会
  public async createGuild(adcode: number) {
    const [, res] = await $service<boolean>({
      url: EnumApiTonghao.Guildcreate,
      method: 'POST',
      params: {
        adcode
      }
    })

    return res === true
  }

  // guild初始化页面
  async initPage(guildId: string, isLogin: boolean) {
    // 拉取分桐详情信息
    const detail = await this.getGuildDetail(guildId)

    if (!detail) return null

    if (!isLogin)
      return {
        detail,
        hasJoin: false,
        isJoinCity: false,
        isManager: false
      }

    // 判断是否加入分桐
    const hasJoin = await this.getHasJoinedCity()

    if (!hasJoin)
      return {
        detail,
        hasJoin: false,
        isJoinCity: false,
        isManager: false
      }

    const { id, memberType } = this.joinedCity!
    // 判断是否加入当前分桐 ｜ 是否是管理员
    const isJoinCity = id === guildId
    const isManager = isJoinCity && memberType === EnumGuildMemberType.Manager

    return {
      detail,
      hasJoin,
      isJoinCity,
      isManager
    }
  }

  @action
  setAllCities(data: Array<AllCities>) {
    this.allCities = data
  }

  @action
  updateJoinedCity(city: Partial<GuildMyself>) {
    if (!this.joinedCity) return

    this.joinedCity = {
      ...this.joinedCity,
      ...city
    }
  }

  @action
  setLocate(data: CityTypeInfo) {
    this.locate = data
  }

  @action
  setRecommend(data: Array<CityTypeInfo>) {
    this.recommend = data
  }

  @action
  setCascadeJson(data: Array<RawCityItem>) {
    this.cascadeJson = data
  }

  @action
  setJoinedCity(city: GuildMyself | null) {
    this.joinedCity = city
  }

  @action
  reset() {
    this.joinedCity = null
    this.cascadeJson = []
    this.recommend = []
    this.locate = null
    this.allCities = []
  }
}
